import { compressImageDataUrl } from "@/cloud/cloud-part-image-fit";
import { normalizeSkillBar } from "@/character/skills/storage";
import type { SkillBarState, SkillDefinition } from "@/character/skills/types";

/** Stay under Firestore's 1 MiB document cap (proto overhead included). */
const CLOUD_DOC_BUDGET = 900_000;

function utf8Size(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function envelopeSize(payload: SkillBarState): number {
  return utf8Size({ savedAt: 0, payload });
}

/** Cloud copy omits bulky editor-only sheet; local IDB keeps the full skill. */
function pickSkillForCloud(skill: SkillDefinition): SkillDefinition {
  const { sourceSheetDataUrl: _source, ...rest } = skill;
  return rest;
}

function pickBarForCloud(bar: SkillBarState): SkillBarState {
  const normalized = normalizeSkillBar(bar);
  return {
    ...normalized,
    library: normalized.library.map(pickSkillForCloud),
  };
}

async function shrinkSkillImages(bar: SkillBarState, maxEdge: number): Promise<SkillBarState> {
  const library = await Promise.all(
    bar.library.map(async (skill) => {
      const spritesheetDataUrl = skill.spritesheetDataUrl.startsWith("data:")
        ? await compressImageDataUrl(skill.spritesheetDataUrl, maxEdge, { pixelArt: true })
        : skill.spritesheetDataUrl;
      const iconDataUrl = skill.iconDataUrl.startsWith("data:")
        ? await compressImageDataUrl(skill.iconDataUrl, Math.min(maxEdge, 128), {
            pixelArt: true,
          })
        : skill.iconDataUrl;
      return { ...skill, spritesheetDataUrl, iconDataUrl };
    }),
  );
  return { ...bar, library };
}

/**
 * Skill cloud docs embed every library spritesheet + icon.
 * Firestore rejects an oversized `payload` as "invalid nested entity".
 */
export async function fitSkillBarForCloud(bar: SkillBarState): Promise<SkillBarState> {
  let payload = pickBarForCloud(bar);
  if (envelopeSize(payload) <= CLOUD_DOC_BUDGET) return payload;

  let maxEdge = 512;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    payload = await shrinkSkillImages(payload, maxEdge);
    if (envelopeSize(payload) <= CLOUD_DOC_BUDGET) return payload;
    maxEdge = Math.round(maxEdge * 0.72);
  }

  throw new Error("คลังสกิลใหญ่เกินที่คลาวด์รับได้");
}
